import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import TeslaButton from '../../components/common/TeslaButton';

interface FilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  filters: {
    specialties: string[];
    maxDistance: number;
    minRating: number;
    priceRange: [number, number];
    availability: string;
    sortBy: string;
    showOnlyNearby?: boolean;
  };
  onFilterChange: (filters: any) => void;
  onApply: () => void;
}

const FilterDrawer: React.FC<FilterDrawerProps> = ({
  isOpen,
  onClose,
  filters,
  onFilterChange,
  onApply
}) => {
  const [localFilters, setLocalFilters] = useState(filters);
  
  // Handle distance change
  const handleDistanceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    setLocalFilters(prev => ({ ...prev, maxDistance: value }));
  };
  
  // Toggle nearby only filter
  const toggleNearbyOnly = () => {
    setLocalFilters(prev => ({ 
      ...prev, 
      showOnlyNearby: !prev.showOnlyNearby 
    }));
  };
  
  // Handle minimum rating change
  const handleRatingChange = (rating: number) => {
    setLocalFilters(prev => ({ 
      ...prev, 
      minRating: prev.minRating === rating ? 0 : rating 
    }));
  };
  
  // Apply filters and close drawer
  const applyFilters = () => {
    onFilterChange(localFilters);
    onApply();
  };
  
  // Reset filters
  const resetFilters = () => {
    const resetValues = {
      specialties: [],
      maxDistance: 50,
      minRating: 0,
      priceRange: [0, 500] as [number, number],
      availability: '',
      sortBy: 'recommended',
      showOnlyNearby: false
    };
    
    setLocalFilters(resetValues);
    onFilterChange(resetValues);
  };
  
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <Backdrop 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          
          <DrawerContainer
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          >
            <DrawerHeader>
              <DrawerTitle>Filters</DrawerTitle>
              <CloseButton onClick={onClose}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </CloseButton>
            </DrawerHeader>
            
            <DrawerContent>
              {/* Distance Slider */}
              <FilterSection>
                <FilterSectionTitle>Distance</FilterSectionTitle>
                <RangeContainer>
                  <RangeLabel>
                    <span>Max Distance</span>
                    <span>{localFilters.maxDistance} miles</span>
                  </RangeLabel>
                  <RangeInput
                    type="range"
                    min="5"
                    max="100"
                    step="5"
                    value={localFilters.maxDistance}
                    onChange={handleDistanceChange}
                  />
                  <RangeValues>
                    <span>5 miles</span>
                    <span>100 miles</span>
                  </RangeValues>
                </RangeContainer>
                
                <ToggleOption>
                  <ToggleLabel>Show only nearby stylists</ToggleLabel>
                  <ToggleSwitch 
                    isChecked={!!localFilters.showOnlyNearby}
                    onClick={toggleNearbyOnly}
                  >
                    <ToggleSlider isChecked={!!localFilters.showOnlyNearby} />
                  </ToggleSwitch>
                </ToggleOption>
              </FilterSection>
              
              <Divider />
              
              {/* Rating Filter */}
              <FilterSection>
                <FilterSectionTitle>Minimum Rating</FilterSectionTitle>
                <RatingSelector>
                  {[1, 2, 3, 4, 5].map(rating => (
                    <RatingStar 
                      key={rating}
                      isSelected={localFilters.minRating >= rating}
                      onClick={() => handleRatingChange(rating)}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                      </svg>
                    </RatingStar>
                  ))}
                  
                  {localFilters.minRating > 0 && (
                    <RatingText>{localFilters.minRating}+ stars</RatingText>
                  )}
                </RatingSelector>
              </FilterSection>
              
              <Divider />
              
              {/* Sort Options */}
              <FilterSection>
                <FilterSectionTitle>Sort By</FilterSectionTitle>
                <SortOptions>
                  <SortOption 
                    isSelected={localFilters.sortBy === 'recommended'}
                    onClick={() => setLocalFilters(prev => ({ ...prev, sortBy: 'recommended' }))}
                  >
                    Recommended
                  </SortOption>
                  <SortOption 
                    isSelected={localFilters.sortBy === 'rating'}
                    onClick={() => setLocalFilters(prev => ({ ...prev, sortBy: 'rating' }))}
                  >
                    Top Rated
                  </SortOption>
                  <SortOption 
                    isSelected={localFilters.sortBy === 'distance'}
                    onClick={() => setLocalFilters(prev => ({ ...prev, sortBy: 'distance' }))}
                  >
                    Nearest
                  </SortOption>
                </SortOptions>
              </FilterSection>
            </DrawerContent>
            
            <DrawerFooter>
              <FooterButton onClick={resetFilters}>
                Reset All
              </FooterButton>
              <TeslaButton 
                variant="primary" 
                onClick={applyFilters}
              >
                Apply Filters
              </TeslaButton>
            </DrawerFooter>
          </DrawerContainer>
        </>
      )}
    </AnimatePresence>
  );
};

// Styled components
const Backdrop = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 100;
`;

const DrawerContainer = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 85%;
  max-width: 400px;
  background-color: #fff;
  z-index: 101;
  display: flex;
  flex-direction: column;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 480px) {
    width: 100%;
  }
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid #eee;
`;

const DrawerTitle = styled.h2`
  font-size: 20px;
  font-weight: 500;
  color: #171a20;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: #f5f5f5;
  }
`;

const DrawerContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
`;

const FilterSection = styled.div`
  margin-bottom: 24px;
`;

const FilterSectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 500;
  color: #171a20;
  margin: 0 0 16px 0;
`;

const RangeContainer = styled.div`
  margin-bottom: 16px;
`;

const RangeLabel = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: #5c5e62;
  margin-bottom: 8px;
`;

const RangeInput = styled.input`
  width: 100%;
  height: 4px;
  background: #ddd;
  border-radius: 4px;
  appearance: none;
  outline: none;
  
  &::-webkit-slider-thumb {
    appearance: none;
    width: 16px;
    height: 16px;
    background: #C9A84C;
    border-radius: 50%;
    cursor: pointer;
  }
  
  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    background: #C9A84C;
    border-radius: 50%;
    cursor: pointer;
    border: none;
  }
`;

const RangeValues = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #8c8c8c;
  margin-top: 8px;
`;

const ToggleOption = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
`;

const ToggleLabel = styled.span`
  font-size: 14px;
  color: #171a20;
`;

const ToggleSwitch = styled.button<{ isChecked: boolean }>`
  position: relative;
  width: 46px;
  height: 24px;
  border-radius: 12px;
  background-color: ${props => props.isChecked ? '#C9A84C' : '#ccc'};
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
`;

const ToggleSlider = styled.span<{ isChecked: boolean }>`
  position: absolute;
  top: 2px;
  left: ${props => props.isChecked ? '24px' : '2px'};
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: white;
  transition: left 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`;

const RatingSelector = styled.div`
  display: flex;
  align-items: center;
`;

const RatingStar = styled.button<{ isSelected: boolean }>`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: ${props => props.isSelected ? '#FFD700' : '#DDD'};
  
  &:hover {
    color: ${props => props.isSelected ? '#FFD700' : '#FFEC8B'};
  }
`;

const RatingText = styled.span`
  font-size: 14px;
  color: #171a20;
  margin-left: 8px;
`;

const Divider = styled.div`
  height: 1px;
  background-color: #eee;
  margin: 0 -20px 24px;
`;

const SortOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const SortOption = styled.button<{ isSelected: boolean }>`
  padding: 10px 16px;
  background-color: ${props => props.isSelected ? '#C9A84C' : '#f5f5f5'};
  color: ${props => props.isSelected ? '#fff' : '#171a20'};
  border: 1px solid ${props => props.isSelected ? '#C9A84C' : '#e0e0e0'};
  border-radius: 40px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    background-color: ${props => props.isSelected ? '#C9A84C' : '#ebebeb'};
  }
`;

const DrawerFooter = styled.div`
  padding: 16px 20px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: space-between;
`;

const FooterButton = styled.button`
  padding: 12px 16px;
  background: none;
  border: 1px solid #e0e0e0;
  border-radius: 40px;
  font-size: 14px;
  font-weight: 500;
  color: #171a20;
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    background-color: #f5f5f5;
  }
`;

export default FilterDrawer;